import type { ReadonlyJSONValue } from "@rocicorp/zero";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import type { StreamEvent } from "../../shared/streamEvents";
import { chatStreamPath } from "../lib/apiPaths";
import { emitErrorToast } from "../lib/errorToast";
import { apiUrl } from "../lib/tenantOrigin";
import { getServerGate } from "./serverGate";

export type StreamMessage = {
  role: "assistant" | "reasoning" | "tool";
  text: string;
  toolInput?: ReadonlyJSONValue;
  status: "streaming" | "done";
};

export type RunStatus = "idle" | "running" | "finished";

type StreamingState = {
  messages: ReadonlyMap<string, StreamMessage>;
  runStatus: RunStatus;
  /**
   * Monotonic count of `run-finished` events seen this mount. Survives the
   * isResponding teardown that resets `runStatus`, giving the queue dispatch an
   * explicit "a run completed" edge to latch on instead of the flicker-prone
   * respondingAt → null signal.
   */
  runCompletions: number;
};

/**
 * Fire-and-forget HTTP POST that broadcasts an abort via Postgres
 * NOTIFY, reaching whichever server is running the AI loop.
 */
export function abortChat(chatId: string, externalApiUrl: string): void {
  fetch(`${externalApiUrl}${chatStreamPath(chatId)}`, {
    method: "POST",
    credentials: "include",
  }).catch(() => {});
}

const FLUSH_INTERVAL_MS = 33;

const EMPTY_MESSAGES: ReadonlyMap<string, StreamMessage> = new Map();
const IDLE_STATE: StreamingState = {
  messages: EMPTY_MESSAGES,
  runStatus: "idle",
  runCompletions: 0,
};

/**
 * Connects to the SSE chat-stream endpoint while the AI is responding.
 * Accumulates deltas into full text on the client side.
 *
 * Content deltas are accumulated in a ref (no re-render) and flushed
 * to React state every ~33ms to avoid excessive re-renders.
 * Lifecycle events (start, end, run-started, run-finished) flush immediately.
 *
 * Returns both the streaming messages map and a `runStatus` derived from
 * SSE lifecycle events, giving the client an immediate signal for state
 * transitions independent of Zero replication latency.
 */
export function useStreamingText(
  chatId: string | null,
  isResponding: boolean,
  onRunFinished?: (messages: ReadonlyMap<string, StreamMessage>) => void
): StreamingState {
  const externalApiUrl = apiUrl();
  const [state, setState] = useState<StreamingState>(IDLE_STATE);
  const accRef = useRef<Map<string, StreamMessage>>(new Map());
  const dirtyRef = useRef(false);
  const runStatusRef = useRef<RunStatus>("idle");
  const runCompletionsRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onRunFinishedRef = useRef(onRunFinished);
  onRunFinishedRef.current = onRunFinished;

  useEffect(() => {
    if (!isResponding || !chatId) return;

    let cancelled = false;
    let source: EventSource | null = null;

    const flush = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setState({
          messages: new Map(accRef.current),
          runStatus: runStatusRef.current,
          runCompletions: runCompletionsRef.current,
        });
      }
    };

    const flushImmediate = () => {
      dirtyRef.current = false;
      setState({
        messages: new Map(accRef.current),
        runStatus: runStatusRef.current,
        runCompletions: runCompletionsRef.current,
      });
    };

    void getServerGate().then(() => {
      if (cancelled) return;

      flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);

      const url = `${externalApiUrl}${chatStreamPath(chatId)}`;
      source = new EventSource(url, { withCredentials: true });

      let errorCount = 0;
      source.onerror = () => {
        errorCount++;
        if (errorCount > 10) {
          source?.close();
          accRef.current = new Map();
          runStatusRef.current = "idle";
          setState({
            messages: EMPTY_MESSAGES,
            runStatus: "idle",
            runCompletions: runCompletionsRef.current,
          });
          Sentry.withScope((scope) => {
            scope.setTag("chat.stream", "connection-lost");
            scope.setExtra("chatId", chatId);
            Sentry.captureException(
              new Error("Lost connection to chat-stream SSE endpoint")
            );
          });
          emitErrorToast({
            icon: "circle-off",
            message: "Lost connection to chat. Please try again.",
            buttonLabel: "Dismiss",
          });
        }
      };

      source.onmessage = (msg) => {
        errorCount = 0;
        let event: StreamEvent;
        try {
          event = JSON.parse(msg.data);
        } catch {
          return;
        }
        const acc = accRef.current;

        switch (event.type) {
          case "run-started":
            runStatusRef.current = "running";
            flushImmediate();
            return;
          case "message-start":
            acc.set(event.messageId, {
              role: event.role,
              text: "",
              status: "streaming",
            });
            flushImmediate();
            return;
          case "message-content": {
            const existing = acc.get(event.messageId);
            if (existing) existing.text += event.delta;
            dirtyRef.current = true;
            return;
          }
          case "tool-call-preview": {
            const existing = acc.get(event.messageId);
            if (existing) existing.toolInput = event.input;
            dirtyRef.current = true;
            return;
          }
          case "message-end": {
            const existing = acc.get(event.messageId);
            if (existing) existing.status = "done";
            flushImmediate();
            return;
          }
          case "run-finished":
            runStatusRef.current = "finished";
            runCompletionsRef.current += 1;
            for (const entry of acc.values()) {
              entry.status = "done";
            }
            flushImmediate();
            onRunFinishedRef.current?.(acc);
            return;
        }
      };
    });

    return () => {
      cancelled = true;
      source?.close();
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
      accRef.current = new Map();
      runStatusRef.current = "idle";
      setState({
        messages: EMPTY_MESSAGES,
        runStatus: "idle",
        runCompletions: runCompletionsRef.current,
      });
    };
  }, [chatId, isResponding, externalApiUrl]);

  return state;
}
